import { 
    AdminLoginDto, 
    AuthResponseDto, 
    LoginDto, 
    User, 
    JanusApiError, 
    Options 
} from "../types";
import { JanusAPIv2Client } from "../client";

/**
 * A type alias for the internal request function passed from the main client.
 * Aligned with JanusAPIv2Client._request signature.
 */
 type RequestExecutor = JanusAPIv2Client['_request'];

/**
 * Type alias for the main client's setToken method.
 */
 type SetTokenFunction = (token: string | null) => void;

/**
 * Client for interacting with the Authentication endpoints of the Janus API.
 */
export class AuthClient {
    private _request: RequestExecutor;
    private _setToken: SetTokenFunction;

    /**
     * Creates an instance of AuthClient.
     * @param requestExecutor The main client's _request method bound to it.
     * @param setTokenFunction The main client's setToken method bound to it.
     */
    constructor(requestExecutor: RequestExecutor, setTokenFunction: SetTokenFunction) {
        this._request = requestExecutor;
        this._setToken = setTokenFunction;
    }

    /**
     * Logs in an administrator using an admin API key.
     * Updates the client's token on success.
     * @param adminKey The admin key string.
     * @returns A promise resolving to the authentication response.
     */
    async adminLogin(adminKey: string): Promise<AuthResponseDto> {
        const loginData: AdminLoginDto = { adminKey };
        const response = await this._request<AuthResponseDto>(
            'POST',
            'auth/admin-login',
            { body: loginData }
        );
        if (response && 'success' in response && response.success === false) {
            this._setToken(null);
            throw new JanusApiError(
                response.message || 'Admin login failed',
                response.statusCode || 401,
                response
            );
        }
        if (response && 'access_token' in response && response.access_token) {
             this._setToken(response.access_token);
        } else {
             this._setToken(null);
        }
        return response;
    }

    /**
     * Logs in a standard user using email and password.
     * Updates the client's token on success.
     * @param credentials The user login credentials { email, password }.
     * @returns A promise resolving to the authentication response.
     */
    async login(credentials: LoginDto): Promise<AuthResponseDto> {
        const response = await this._request<AuthResponseDto>(
            'POST',
            'auth/login',
            { body: credentials }
        );
        if (response && 'success' in response && response.success === false) {
            this._setToken(null);
             throw new JanusApiError(
                response.message || 'Login failed',
                response.statusCode || 401,
                response
            );
        }
        if (response && 'access_token' in response && response.access_token) {
             this._setToken(response.access_token);
        } else {
             this._setToken(null); 
        }
        return response;
    }

    /**
     * Retrieves the profile of the currently authenticated user.
     * Requires the client to be authenticated.
     * @returns A promise resolving to the user's profile information.
     */
    async getProfile(): Promise<User> {
        const userProfile = await this._request<User>(
            'GET',
            'auth/profile',
            {}
        );
        return userProfile;
    }

    // Add other auth methods here (e.g., regular user login, logout, refresh token)
} 